import { Link } from "react-router-dom";
import { MapPin, MoreHorizontal, Box, CalendarRange, Pencil, GanttChart, Trash2, Users, Landmark, Send, Play, Calculator, Truck, Smartphone, Wrench, History, ChevronRight } from "lucide-react";
import { fmtDate } from "@/lib/format";
import { PROJECT_STATUSES } from "@/lib/labels";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { StatusChanger } from "@/components/common/StatusChanger";
import { StatusBadge } from "@/components/common/StatusBadge";
import { CompanyMark } from "@/components/common/CompanyMark";

const PRIMARY_ICON = { rig: Box, allocate: Box, transport: Truck, "transport-tab": Truck, task: Pencil, innkvartering: Users, service: Wrench, history: History, permit: Landmark, quote: Send, start: Play, calc: Calculator, timeline: GanttChart, field: Smartphone };

/**
 * Condensed identity shown in the app topbar once the page header has scrolled away (GitHub/Vercel pattern):
 * «Prosjekter › Navn · fase». Keeps context on long tabs (kalender, modultabellar) without a second header.
 */
export const ProjectCondensedIdentity = ({ p }) => (
  <div className="flex min-w-0 items-center gap-1.5 text-[13px] fade-up" data-testid="project-topbar-identity">
    <Link to="/prosjekter" className="hidden shrink-0 rounded text-muted-foreground transition-colors duration-150 hover:text-foreground focus-visible:text-foreground focus-visible:outline-none sm:inline">Prosjekter</Link>
    <ChevronRight className="hidden h-3.5 w-3.5 shrink-0 text-muted-foreground/60 sm:inline" aria-hidden />
    <span className="hidden shrink-0 font-mono text-[11.5px] tabular text-muted-foreground md:inline">{p.project_number}</span>
    <CompanyMark kind="customer" id={p.customer_id} name={p.customer_name} size="xs" className="ml-0.5" testId="project-topbar-mark" />
    <span className="min-w-0 truncate font-medium" data-testid="project-topbar-identity-name">{p.name}</span>
    <span className="hidden sm:inline-flex"><StatusBadge status={p.status} entity="project" variant="pill" /></span>
  </div>
);

/**
 * Prosjektside 2.0 header: identity row (title + phase chip + meta) and ONE phase-aware primary action.
 * Everything else lives in the «···» menu. The phase chip *is* the status changer for users who may write.
 */
export const ProjectHeader = ({ p, lens, can, onPrimary, onAction, deleteDisabledReason }) => {
  const pri = lens.primary;
  const allowed = !pri.perm || can(pri.perm);
  const Icon = PRIMARY_ICON[pri.key] || Box;
  return (
    <header className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between" data-testid="project-header">
      <div className="flex min-w-0 items-start gap-3.5">
        <CompanyMark kind="customer" id={p.customer_id} name={p.customer_name} size="xl" className="mt-0.5 hidden sm:inline-flex" testId="project-header-mark" />
        <div className="min-w-0">
        <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-2">
          <h1 className="font-display min-w-0 text-[24px] font-semibold leading-8 tracking-tight sm:text-[28px] sm:leading-9" data-testid="project-title">{p.name}</h1>
          <span className="inline-flex items-center" data-testid="project-phase-chip">
            {can("projects:write") ? (
              <StatusChanger endpoint={`/projects/${p.id}/status`} status={p.status} statuses={PROJECT_STATUSES} onChanged={onAction.changed} testId="project-status-select"
                className="h-7 w-auto gap-1.5 rounded-full border-border/70 bg-muted/60 px-2.5 text-[12px] font-medium shadow-none hover:bg-muted focus:ring-1 focus:ring-primary/40 [&>svg]:h-3.5 [&>svg]:w-3.5 [&>svg]:opacity-60" />
            ) : (
              <StatusBadge status={p.status} entity="project" variant="pill" />
            )}
          </span>
          <span className="font-mono text-[11.5px] tabular text-muted-foreground" title="Fase">{lens.idx + 1}/{PROJECT_STATUSES.length}</span>
        </div>
        <div className="mt-1.5 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[13px] text-muted-foreground" data-testid="project-meta-line">
          <span className="font-mono text-[12.5px] tabular">{p.project_number}</span>
          <span className="text-border">·</span>
          {can("customers:read") ? <Link to={`/kunder/${p.customer_id}`} className="text-foreground/85 underline-offset-[3px] hover:underline" data-testid="project-customer-link">{p.customer_name}</Link> : <span className="text-foreground/85">{p.customer_name}</span>}
          {p.address && <><span className="text-border">·</span><span className="inline-flex min-w-0 items-center gap-1"><MapPin className="h-3.5 w-3.5 shrink-0" /><span className="truncate">{p.address}</span></span></>}
          <span className="text-border">·</span>
          <span className="font-mono text-[12.5px] tabular">{fmtDate(p.start_date, "d. MMM yyyy")} – {fmtDate(p.end_date, "d. MMM yyyy")}</span>
        </div>
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-2" data-testid="project-actions">
        {allowed && (
          <Button onClick={() => onPrimary(pri.key)} className="h-9 active:scale-[0.98]" data-testid="project-primary-action-button"><Icon /> {pri.label}</Button>
        )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="icon" className="h-9 w-9" aria-label="Flere handlinger" data-testid="project-more-menu"><MoreHorizontal /></Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-60" data-testid="project-more-menu-content">
            <DropdownMenuLabel className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Prosjekt</DropdownMenuLabel>
            {can("modules:read") && <DropdownMenuItem onSelect={() => onAction.rig()} data-testid="project-open-rig-button"><Box /> Riggdesign</DropdownMenuItem>}
            {can("planning:read") && <DropdownMenuItem onSelect={() => onAction.timeline()} data-testid="project-open-planning-button"><GanttChart /> Tidslinje</DropdownMenuItem>}
            {can("planning:write") && <DropdownMenuItem onSelect={() => onAction.reschedule()} data-testid="project-reschedule-button"><CalendarRange /> Flytt datoer <span className="ml-auto text-[11px] text-muted-foreground">konsekvens</span></DropdownMenuItem>}
            {can("projects:write") && <DropdownMenuItem onSelect={() => onAction.edit()} data-testid="project-edit-button"><Pencil /> Rediger detaljer</DropdownMenuItem>}
            {can("projects:write") && (
              <>
                <DropdownMenuSeparator />
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div>
                      <DropdownMenuItem disabled={Boolean(deleteDisabledReason)} onSelect={() => onAction.remove()} className={cn(!deleteDisabledReason && "text-destructive focus:text-destructive")} data-testid="project-delete-button"><Trash2 /> Slett prosjekt</DropdownMenuItem>
                    </div>
                  </TooltipTrigger>
                  {deleteDisabledReason && <TooltipContent side="left">{deleteDisabledReason}</TooltipContent>}
                </Tooltip>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};
